import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { practiceScheduleAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  ArrowLeft,
  Plus,
  Download,
  Save,
  Trash2,
  Clock,
  GripVertical,
  Pencil,
  FileText,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'OL', 'DB', 'LB', 'DL'];

const DRILL_PRESETS = [
  'SPECIAL TEAMS',
  'INDY / GROUP',
  'CONCEPT',
  'EDGES',
  'TEAM DEATH ZONE',
  'TEAM INSTALL',
  'TEAM RED ZONE',
  'TEAM PASS',
  'TEAM BLITZ',
  'CONDITIONING',
  'WARM UP',
  'STRETCH',
  'INDIVIDUAL',
  'POSITION WORK',
  '7 ON 7',
  '9 ON 7',
  'INSIDE RUN',
  'TEAM RUN',
  'SITUATIONAL',
  '1v1 DB/WR',
  '1v1 OL/DL'
];

const DAY_TYPES = [
  { value: 'green', label: 'Green - Fundamentals, Habits', color: 'bg-[#32D74B]' },
  { value: 'black', label: 'Black - PHYSICAL / ATTITUDE', color: 'bg-[#27272A] border border-[#52525B]' },
  { value: 'orange', label: 'Orange - Recovery focused', color: 'bg-[#FF9500]' },
];

const FORMATIONS = [
  'SHOTGUN', 'PISTOL', 'I-FORM', 'SINGLEBACK', 'EMPTY', 'GOAL LINE', 'WILDCAT', 'ACE'
];

const PERSONNEL = [
  '10', '11', '12', '13', '20', '21', '22', '23'
];

const HASH_MARKS = [
  { value: 'L', label: 'Left' },
  { value: 'M', label: 'Middle' },
  { value: 'R', label: 'Right' },
];

const SITUATIONS = [
  '', 'RED ZONE', '3RD DOWN', '2-MINUTE', 'GOAL LINE', 'BACKED UP', 'OPENING', '4TH DOWN'
];

export default function PracticeScheduleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Period editing
  const [periodModal, setPeriodModal] = useState({ open: false, period: null, index: -1 });
  const [periodForm, setPeriodForm] = useState({
    period_number: 1,
    duration_minutes: 10,
    drill_name: 'TEAM INSTALL',
    period_type: 'team',
    total_plays: '',
    position_drills: POSITIONS.map(pos => ({ position: pos, drills: [] })),
    notes: ''
  });

  // Meeting editing
  const [meetingModal, setMeetingModal] = useState({ open: false, meeting: null, index: -1 });
  const [meetingForm, setMeetingForm] = useState({
    time: '7:30 AM',
    name: '',
    duration_minutes: 30
  });

  // Script editing
  const [activeTab, setActiveTab] = useState('periods'); // 'periods' or 'script'
  const [scriptModal, setScriptModal] = useState({ open: false, play: null, index: -1 });
  const [scriptForm, setScriptForm] = useState({
    play_number: 1,
    formation: 'SHOTGUN',
    personnel: '11',
    play_name: '',
    hash_mark: 'M',
    down: 1,
    distance: '10',
    situation: '',
    offensive_call: '',
    defensive_look: '',
    notes: ''
  });

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const res = await practiceScheduleAPI.getOne(id);
      setSchedule(res.data);
    } catch (error) {
      toast.error('Failed to load practice schedule');
      navigate('/practice');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean up periods before saving - convert empty strings to null for optional int fields
      const cleanedSchedule = {
        ...schedule,
        periods: schedule.periods?.map(p => ({
          ...p,
          total_plays: p.total_plays === '' || p.total_plays === null || p.total_plays === undefined 
            ? null 
            : parseInt(p.total_plays, 10) || null,
          notes: p.notes || null
        })) || []
      };
      
      await practiceScheduleAPI.update(id, cleanedSchedule);
      toast.success('Practice schedule saved');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await practiceScheduleAPI.getPDF(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `practice_plan_${schedule.date}_${schedule.practice_number.replace(/\s+/g, '_').replace('#', '')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const updateScheduleField = (field, value) => {
    setSchedule(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Period functions
  const openAddPeriod = () => {
    const nextNumber = (schedule?.periods?.length || 0) + 1;
    setPeriodForm({
      period_number: nextNumber,
      duration_minutes: 10,
      drill_name: 'TEAM INSTALL',
      period_type: 'team',
      position_drills: POSITIONS.map(pos => ({ position: pos, drills: [] })),
      notes: ''
    });
    setPeriodModal({ open: true, period: null, index: -1 });
  };

  const openEditPeriod = (period, index) => {
    // Ensure all positions exist
    const positionDrills = POSITIONS.map(pos => {
      const existing = period.position_drills?.find(pd => pd.position === pos);
      return existing || { position: pos, drills: [] };
    });
    
    setPeriodForm({
      ...period,
      total_plays: period.total_plays || '',
      position_drills: positionDrills
    });
    setPeriodModal({ open: true, period, index });
  };

  const savePeriod = () => {
    const newPeriods = [...(schedule.periods || [])];
    
    if (periodModal.index >= 0) {
      newPeriods[periodModal.index] = periodForm;
    } else {
      newPeriods.push(periodForm);
    }
    
    // Recalculate total minutes
    const totalMinutes = newPeriods.reduce((sum, p) => sum + p.duration_minutes, 0);
    
    setSchedule(prev => ({ 
      ...prev, 
      periods: newPeriods,
      total_minutes: totalMinutes
    }));
    setHasChanges(true);
    setPeriodModal({ open: false, period: null, index: -1 });
  };

  const deletePeriod = (index) => {
    const newPeriods = schedule.periods.filter((_, i) => i !== index);
    // Renumber periods
    const renumbered = newPeriods.map((p, i) => ({ ...p, period_number: i + 1 }));
    const totalMinutes = renumbered.reduce((sum, p) => sum + p.duration_minutes, 0);
    
    setSchedule(prev => ({ 
      ...prev, 
      periods: renumbered,
      total_minutes: totalMinutes
    }));
    setHasChanges(true);
  };

  const updatePositionDrill = (posIndex, drillText) => {
    const newPositionDrills = [...periodForm.position_drills];
    newPositionDrills[posIndex] = {
      ...newPositionDrills[posIndex],
      drills: drillText.split('\n').filter(d => d.trim())
    };
    setPeriodForm(prev => ({ ...prev, position_drills: newPositionDrills }));
  };

  // Meeting functions
  const openAddMeeting = () => {
    setMeetingForm({ time: '7:30 AM', name: '', duration_minutes: 30 });
    setMeetingModal({ open: true, meeting: null, index: -1 });
  };

  const openEditMeeting = (meeting, index) => {
    setMeetingForm(meeting);
    setMeetingModal({ open: true, meeting, index });
  };

  const saveMeeting = () => {
    const newMeetings = [...(schedule.meetings || [])];
    
    if (meetingModal.index >= 0) {
      newMeetings[meetingModal.index] = meetingForm;
    } else {
      newMeetings.push(meetingForm);
    }
    
    setSchedule(prev => ({ ...prev, meetings: newMeetings }));
    setHasChanges(true);
    setMeetingModal({ open: false, meeting: null, index: -1 });
  };

  const deleteMeeting = (index) => {
    const newMeetings = schedule.meetings.filter((_, i) => i !== index);
    setSchedule(prev => ({ ...prev, meetings: newMeetings }));
    setHasChanges(true);
  };

  // Script functions
  const openAddScript = () => {
    const nextNumber = (schedule?.script?.length || 0) + 1;
    setScriptForm({
      play_number: nextNumber,
      formation: 'SHOTGUN',
      personnel: '11',
      play_name: '',
      hash_mark: 'M',
      down: 1,
      distance: '10',
      situation: '',
      offensive_call: '',
      defensive_look: '',
      notes: ''
    });
    setScriptModal({ open: true, play: null, index: -1 });
  };

  const openEditScript = (play, index) => {
    setScriptForm(play);
    setScriptModal({ open: true, play, index });
  };

  const saveScript = () => {
    const newScript = [...(schedule.script || [])];
    
    if (scriptModal.index >= 0) {
      newScript[scriptModal.index] = scriptForm;
    } else {
      newScript.push(scriptForm);
    }
    
    setSchedule(prev => ({ ...prev, script: newScript }));
    setHasChanges(true);
    setScriptModal({ open: false, play: null, index: -1 });
  };

  const deleteScript = (index) => {
    const newScript = schedule.script.filter((_, i) => i !== index);
    // Renumber plays
    const renumbered = newScript.map((p, i) => ({ ...p, play_number: i + 1 }));
    setSchedule(prev => ({ ...prev, script: renumbered }));
    setHasChanges(true);
  };

  const getDayTypeInfo = (dayType) => {
    return DAY_TYPES.find(d => d.value === dayType) || DAY_TYPES[1];
  };

  if (loading || !schedule) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
        </div>
      </Layout>
    );
  }

  const dayTypeInfo = getDayTypeInfo(schedule.day_type);

  return (
    <Layout>
      <div className="space-y-6" data-testid="practice-editor-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/practice')}
              className="text-[#71717A] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-xs text-[#71717A] uppercase tracking-wider">Practice Plan</p>
              <h1 className="font-display text-3xl text-white tracking-tight">{schedule.practice_number}</h1>
            </div>
            <div className={`w-4 h-4 rounded-full ${dayTypeInfo.color}`} title={dayTypeInfo.label}></div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleDownloadPDF}
              className="bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white disabled:opacity-50"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Practice Info Card */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="text-white text-sm">Practice Details</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <Label className="text-xs text-[#71717A]">Date</Label>
                <Input
                  type="date"
                  value={schedule.date}
                  onChange={(e) => {
                    const d = new Date(e.target.value + 'T12:00:00');
                    updateScheduleField('date', e.target.value);
                    updateScheduleField('day_of_week', format(d, 'EEEE').toUpperCase());
                  }}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-[#71717A]">Day</Label>
                <Input
                  value={schedule.day_of_week}
                  readOnly
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 h-9 opacity-70"
                />
              </div>
              <div>
                <Label className="text-xs text-[#71717A]">Start Time</Label>
                <Input
                  value={schedule.start_time}
                  onChange={(e) => updateScheduleField('start_time', e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-[#71717A]">Field</Label>
                <Input
                  value={schedule.field}
                  onChange={(e) => updateScheduleField('field', e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-[#71717A]">Attire</Label>
                <Input
                  value={schedule.attire}
                  onChange={(e) => updateScheduleField('attire', e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1 h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-[#71717A]">Day Type</Label>
                <div className="flex gap-1 mt-1">
                  {DAY_TYPES.map(type => (
                    <button
                      key={type.value}
                      onClick={() => updateScheduleField('day_type', type.value)}
                      className={`flex-1 h-9 ${type.color} ${schedule.day_type === type.value ? 'ring-2 ring-[#FF3B30]' : ''}`}
                      title={type.label}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs text-[#71717A]">Situational Emphasis</Label>
              <Input
                value={schedule.situational_emphasis || ''}
                onChange={(e) => updateScheduleField('situational_emphasis', e.target.value)}
                placeholder="e.g., 3rd Down Conversions, Red Zone Efficiency"
                className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Meetings Section */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A] flex flex-row items-center justify-between">
            <CardTitle className="text-white text-sm">Meetings</CardTitle>
            <Button
              size="sm"
              onClick={openAddMeeting}
              className="bg-[#27272A] hover:bg-[#3F3F46] text-white"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Meeting
            </Button>
          </CardHeader>
          <CardContent className="p-4">
            {schedule.meetings?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {schedule.meetings.map((meeting, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 bg-[#0A0A0A] border border-[#27272A] px-3 py-2 group"
                  >
                    <Clock className="w-4 h-4 text-[#71717A]" />
                    <span className="text-white text-sm">
                      {meeting.time} - {meeting.name}
                      {meeting.duration_minutes && <span className="text-[#71717A]"> ({meeting.duration_minutes}min)</span>}
                    </span>
                    <button
                      onClick={() => openEditMeeting(meeting, index)}
                      className="opacity-0 group-hover:opacity-100 text-[#71717A] hover:text-white"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteMeeting(index)}
                      className="opacity-0 group-hover:opacity-100 text-[#FF3B30]"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#71717A] text-sm">No meetings scheduled. Add pre-practice meetings, positional meetings, etc.</p>
            )}
          </CardContent>
        </Card>

        {/* Periods Section */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A] flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="text-white text-sm">Practice Periods</CardTitle>
              <span className="text-[#71717A] text-sm">
                Total: <span className="text-[#FF3B30] font-bold">{schedule.total_minutes}</span> mins
              </span>
            </div>
            <Button
              size="sm"
              onClick={openAddPeriod}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              data-testid="add-period-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Period
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {schedule.periods?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#27272A] bg-[#1a237e]">
                      <th className="p-2 text-left text-white font-bold w-12">#</th>
                      <th className="p-2 text-left text-white font-bold w-16">MIN</th>
                      <th className="p-2 text-left text-white font-bold w-32">DRILL</th>
                      <th className="p-2 text-center text-white font-bold w-16">PLAYS</th>
                      {POSITIONS.map(pos => (
                        <th key={pos} className="p-2 text-center text-white font-bold w-20">{pos}</th>
                      ))}
                      <th className="p-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.periods.map((period, index) => (
                      <tr 
                        key={index} 
                        className={`border-b border-[#27272A] hover:bg-[#1A1A1A] ${index % 2 === 0 ? 'bg-[#0A0A0A]' : 'bg-[#121212]'}`}
                      >
                        <td className="p-2 text-white font-mono">{period.period_number}</td>
                        <td className="p-2 text-[#FF3B30] font-mono">:{period.duration_minutes.toString().padStart(2, '0')}</td>
                        <td className="p-2 text-white font-medium">{period.drill_name}</td>
                        <td className="p-2 text-center text-[#FFD60A] font-mono">{period.total_plays || '-'}</td>
                        {POSITIONS.map(pos => {
                          const posDrill = period.position_drills?.find(pd => pd.position === pos);
                          return (
                            <td key={pos} className="p-2 text-center text-[#A1A1AA] text-xs">
                              {posDrill?.drills?.slice(0, 2).join('\n') || '-'}
                            </td>
                          );
                        })}
                        <td className="p-2">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => openEditPeriod(period, index)}
                              className="p-1 text-[#71717A] hover:text-white"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletePeriod(index)}
                              className="p-1 text-[#71717A] hover:text-[#FF3B30]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#71717A]">
                <p className="text-lg">No periods yet</p>
                <p className="text-sm">Add practice periods to build your schedule</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Practice Script Section */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="border-b border-[#27272A] flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Play className="w-4 h-4 text-[#32D74B]" />
                Practice Script
              </CardTitle>
              <span className="text-[#71717A] text-sm">
                {schedule.script?.length || 0} plays
              </span>
            </div>
            <Button
              size="sm"
              onClick={openAddScript}
              className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
              data-testid="add-script-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Play
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {schedule.script?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#27272A] bg-[#2e7d32]">
                      <th className="p-2 text-left text-white font-bold w-10">#</th>
                      <th className="p-2 text-left text-white font-bold w-24">FORM</th>
                      <th className="p-2 text-left text-white font-bold w-12">PERS</th>
                      <th className="p-2 text-left text-white font-bold">PLAY</th>
                      <th className="p-2 text-center text-white font-bold w-12">HASH</th>
                      <th className="p-2 text-center text-white font-bold w-10">DN</th>
                      <th className="p-2 text-center text-white font-bold w-12">DIST</th>
                      <th className="p-2 text-left text-white font-bold w-24">SITUATION</th>
                      <th className="p-2 text-left text-white font-bold">NOTES</th>
                      <th className="p-2 w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.script.map((play, index) => (
                      <tr 
                        key={index} 
                        className={`border-b border-[#27272A] hover:bg-[#1A1A1A] ${index % 2 === 0 ? 'bg-[#0A0A0A]' : 'bg-[#121212]'}`}
                      >
                        <td className="p-2 text-white font-mono">{play.play_number}</td>
                        <td className="p-2 text-[#32D74B] font-medium">{play.formation}</td>
                        <td className="p-2 text-[#71717A]">{play.personnel}</td>
                        <td className="p-2 text-white font-medium">{play.play_name}</td>
                        <td className="p-2 text-center text-[#FF9500]">{play.hash_mark}</td>
                        <td className="p-2 text-center text-white">{play.down}</td>
                        <td className="p-2 text-center text-[#71717A]">{play.distance}</td>
                        <td className="p-2 text-[#007AFF] text-xs">{play.situation}</td>
                        <td className="p-2 text-[#71717A] text-xs truncate max-w-[150px]">{play.notes}</td>
                        <td className="p-2">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => openEditScript(play, index)}
                              className="p-1 text-[#71717A] hover:text-white"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteScript(index)}
                              className="p-1 text-[#71717A] hover:text-[#FF3B30]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-[#71717A]">
                <Play className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-lg">No plays in script</p>
                <p className="text-sm">Add plays to build your practice script</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Period Modal */}
        <Dialog open={periodModal.open} onOpenChange={(open) => setPeriodModal({ ...periodModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{periodModal.index >= 0 ? 'Edit Period' : 'Add Period'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Period #</Label>
                  <Input
                    type="number"
                    value={periodForm.period_number}
                    onChange={(e) => setPeriodForm({ ...periodForm, period_number: parseInt(e.target.value) || 1 })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Duration (min)</Label>
                  <Input
                    type="number"
                    value={periodForm.duration_minutes}
                    onChange={(e) => setPeriodForm({ ...periodForm, duration_minutes: parseInt(e.target.value) || 5 })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Drill Name</Label>
                  <select
                    value={periodForm.drill_name}
                    onChange={(e) => setPeriodForm({ ...periodForm, drill_name: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  >
                    {DRILL_PRESETS.map(drill => (
                      <option key={drill} value={drill}>{drill}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Total Plays</Label>
                  <Input
                    type="number"
                    value={periodForm.total_plays || ''}
                    onChange={(e) => setPeriodForm({ ...periodForm, total_plays: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Optional"
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                    data-testid="total-plays-input"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Position Drills</Label>
                <div className="grid grid-cols-5 gap-3 mt-2">
                  {POSITIONS.map((pos, posIndex) => (
                    <div key={pos}>
                      <Label className="text-xs text-[#71717A]">{pos}</Label>
                      <textarea
                        value={periodForm.position_drills[posIndex]?.drills?.join('\n') || ''}
                        onChange={(e) => updatePositionDrill(posIndex, e.target.value)}
                        placeholder="Drill 1&#10;Drill 2"
                        rows={3}
                        className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-2 py-1 mt-1 text-xs resize-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-[#A1A1AA]">Notes (Optional)</Label>
                <Input
                  value={periodForm.notes || ''}
                  onChange={(e) => setPeriodForm({ ...periodForm, notes: e.target.value })}
                  placeholder="Any additional notes for this period"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPeriodModal({ open: false, period: null, index: -1 })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={savePeriod}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                data-testid="save-period-btn"
              >
                {periodModal.index >= 0 ? 'Update Period' : 'Add Period'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Meeting Modal */}
        <Dialog open={meetingModal.open} onOpenChange={(open) => setMeetingModal({ ...meetingModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>{meetingModal.index >= 0 ? 'Edit Meeting' : 'Add Meeting'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Time</Label>
                <Input
                  value={meetingForm.time}
                  onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })}
                  placeholder="e.g., 7:30 AM"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Meeting Name</Label>
                <Input
                  value={meetingForm.name}
                  onChange={(e) => setMeetingForm({ ...meetingForm, name: e.target.value })}
                  placeholder="e.g., SPECIAL TEAMS MEETING, TEAM MEETING"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Duration (minutes)</Label>
                <Input
                  type="number"
                  value={meetingForm.duration_minutes || ''}
                  onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(e.target.value) || null })}
                  placeholder="Optional"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setMeetingModal({ open: false, meeting: null, index: -1 })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={saveMeeting}
                className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              >
                {meetingModal.index >= 0 ? 'Update Meeting' : 'Add Meeting'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Script Modal */}
        <Dialog open={scriptModal.open} onOpenChange={(open) => setScriptModal({ ...scriptModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Play className="w-5 h-5 text-[#32D74B]" />
                {scriptModal.index >= 0 ? 'Edit Play' : 'Add Play'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Row 1: Play #, Formation, Personnel */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Play #</Label>
                  <Input
                    type="number"
                    value={scriptForm.play_number}
                    onChange={(e) => setScriptForm({ ...scriptForm, play_number: parseInt(e.target.value) || 1 })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Formation</Label>
                  <select
                    value={scriptForm.formation}
                    onChange={(e) => setScriptForm({ ...scriptForm, formation: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  >
                    {FORMATIONS.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Personnel</Label>
                  <select
                    value={scriptForm.personnel}
                    onChange={(e) => setScriptForm({ ...scriptForm, personnel: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  >
                    {PERSONNEL.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Play Name */}
              <div>
                <Label className="text-[#A1A1AA]">Play Name</Label>
                <Input
                  value={scriptForm.play_name}
                  onChange={(e) => setScriptForm({ ...scriptForm, play_name: e.target.value.toUpperCase() })}
                  placeholder="e.g., ZONE LEFT, POWER RIGHT, PA BOOT"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  data-testid="play-name-input"
                />
              </div>

              {/* Row 3: Hash, Down, Distance, Situation */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Hash Mark</Label>
                  <div className="flex gap-1 mt-1">
                    {HASH_MARKS.map(h => (
                      <button
                        key={h.value}
                        type="button"
                        onClick={() => setScriptForm({ ...scriptForm, hash_mark: h.value })}
                        className={`flex-1 py-2 border text-center text-sm ${
                          scriptForm.hash_mark === h.value
                            ? 'border-[#FF9500] bg-[#FF9500]/10 text-[#FF9500]'
                            : 'border-[#27272A] bg-[#0A0A0A] text-white'
                        }`}
                      >
                        {h.value}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Down</Label>
                  <select
                    value={scriptForm.down}
                    onChange={(e) => setScriptForm({ ...scriptForm, down: parseInt(e.target.value) })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  >
                    {[1, 2, 3, 4].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Distance</Label>
                  <Input
                    value={scriptForm.distance}
                    onChange={(e) => setScriptForm({ ...scriptForm, distance: e.target.value })}
                    placeholder="10, Goal, 6+"
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Situation</Label>
                  <select
                    value={scriptForm.situation}
                    onChange={(e) => setScriptForm({ ...scriptForm, situation: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] text-white px-3 py-2 mt-1"
                  >
                    {SITUATIONS.map(s => (
                      <option key={s} value={s}>{s || '(None)'}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 4: Offensive Call */}
              <div>
                <Label className="text-[#A1A1AA]">Full Offensive Call (optional)</Label>
                <Input
                  value={scriptForm.offensive_call}
                  onChange={(e) => setScriptForm({ ...scriptForm, offensive_call: e.target.value.toUpperCase() })}
                  placeholder="e.g., GUN TRIPS RT Z MOTION MESH"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>

              {/* Row 5: Defensive Look */}
              <div>
                <Label className="text-[#A1A1AA]">Expected Defensive Look (optional)</Label>
                <Input
                  value={scriptForm.defensive_look}
                  onChange={(e) => setScriptForm({ ...scriptForm, defensive_look: e.target.value.toUpperCase() })}
                  placeholder="e.g., COVER 3, MAN PRESS, ZONE BLITZ"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>

              {/* Row 6: Notes */}
              <div>
                <Label className="text-[#A1A1AA]">Notes</Label>
                <Input
                  value={scriptForm.notes || ''}
                  onChange={(e) => setScriptForm({ ...scriptForm, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setScriptModal({ open: false, play: null, index: -1 })}
                className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
              >
                Cancel
              </Button>
              <Button
                onClick={saveScript}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
                data-testid="save-script-btn"
              >
                {scriptModal.index >= 0 ? 'Update Play' : 'Add Play'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
